import { useState } from "react";
import { Loader2, Sparkles, Check, X, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { proposeEditorial, applyEditorial, type EditorialTool } from "@/lib/contentOps/api";
import type { ArticleRow } from "@/lib/contentOps/types";

interface Props {
  article: ArticleRow;
  onApplied: () => Promise<void> | void;
}

type Proposal = {
  tool: EditorialTool;
  model: string;
  section_index: number | null;
  proposal: Record<string, unknown>;
};

const TOOLS: { id: EditorialTool; label: string; needsSection: boolean; hint: string }[] = [
  { id: "rewrite_intro",      label: "Rewrite intro",       needsSection: false, hint: "Rewrites the first paragraph of section 1." },
  { id: "humanize_section",   label: "Humanize section",    needsSection: true,  hint: "Strips AI tone, adds specifics." },
  { id: "eeat_enhance",       label: "Enhance E-E-A-T",     needsSection: true,  hint: "Adds first-person experience + authority." },
  { id: "tighten_cta",        label: "Tighten CTA",         needsSection: true,  hint: "Replaces vague CTAs with outcomes." },
  { id: "snippet_optimize",   label: "Optimize for snippet", needsSection: true, hint: "Restructures for featured-snippet shape." },
  { id: "title_alternatives", label: "Title alternatives",  needsSection: false, hint: "Returns 5 alt SEO titles." },
  { id: "meta_optimize",      label: "Optimize meta",       needsSection: false, hint: "Returns 3 alt meta descriptions." },
];

const EditorialTools = ({ article, onApplied }: Props) => {
  const [running, setRunning] = useState<EditorialTool | null>(null);
  const [section, setSection] = useState<number>(0);
  const [extra, setExtra] = useState("");
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [applying, setApplying] = useState(false);
  const [pickedTitle, setPickedTitle] = useState<string | null>(null);
  const [pickedMeta, setPickedMeta] = useState<string | null>(null);

  const sections = article.content ?? [];

  const run = async (tool: EditorialTool, needsSection: boolean) => {
    if (needsSection && !sections[section]) {
      toast.error("Pick a valid section first");
      return;
    }
    setRunning(tool);
    setProposal(null);
    setPickedTitle(null);
    setPickedMeta(null);
    try {
      const res = await proposeEditorial({
        article_id: article.id,
        tool,
        section_index: needsSection ? section : undefined,
        extra: extra.trim() || undefined,
      });
      setProposal(res);
    } catch (e) {
      toast.error("AI failed", { description: (e as Error).message });
    } finally {
      setRunning(null);
    }
  };

  const apply = async () => {
    if (!proposal) return;
    setApplying(true);
    try {
      const patch = buildPatch(proposal, article, { pickedTitle, pickedMeta });
      if (!patch) {
        toast.error("Pick an option first");
        setApplying(false);
        return;
      }
      await applyEditorial({
        article_id: article.id,
        patch,
        revision_type: `editorial_${proposal.tool}`,
        source_tool: proposal.tool,
        ai_model: proposal.model,
      });
      toast.success("Applied", { description: `${proposal.tool} · ${proposal.model}` });
      setProposal(null);
      await onApplied();
    } catch (e) {
      toast.error("Apply failed", { description: (e as Error).message });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "hsl(0 0% 90%)" }}>
            <Wand2 size={14} /> Editorial AI tools
          </h3>
          <p className="text-[11px] mt-0.5" style={{ color: "hsl(0 0% 50%)" }}>
            All actions return a proposal. Nothing applies until you accept.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-[1fr_auto] gap-3 mb-3">
        <Select value={String(section)} onValueChange={(v) => setSection(Number(v))}>
          <SelectTrigger className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }}>
            <SelectValue placeholder="Section target" />
          </SelectTrigger>
          <SelectContent>
            {sections.length === 0 && <SelectItem value="0" disabled>No sections yet</SelectItem>}
            {sections.map((s, i) => (
              <SelectItem key={i} value={String(i)}>{i + 1}. {s.heading || "(untitled)"} · {s.level?.toUpperCase()}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Textarea
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          rows={1}
          placeholder="Optional extra direction (e.g. 'lead with the 30-day result')"
          className="resize-none text-xs border"
          style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 88%)", minWidth: 260 }}
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {TOOLS.map((t) => (
          <Button
            key={t.id}
            size="sm"
            variant="outline"
            disabled={running !== null}
            onClick={() => run(t.id, t.needsSection)}
            title={t.hint}
            className="border h-8 text-xs"
            style={{ borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)", background: "hsl(0 0% 5%)" }}
          >
            {running === t.id
              ? <Loader2 size={12} className="animate-spin mr-1" />
              : <Sparkles size={12} className="mr-1" style={{ color: "hsl(207 68% 65%)" }} />}
            {t.label}
          </Button>
        ))}
      </div>

      <Dialog open={!!proposal} onOpenChange={(o) => !o && setProposal(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="capitalize">{proposal?.tool?.split("_").join(" ")}</DialogTitle>
            <DialogDescription>
              Generated by <span className="font-mono">{proposal?.model}</span>. Review before applying.
            </DialogDescription>
          </DialogHeader>

          {proposal && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <ProposalView
                proposal={proposal}
                article={article}
                pickedTitle={pickedTitle}
                pickedMeta={pickedMeta}
                onPickTitle={setPickedTitle}
                onPickMeta={setPickedMeta}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setProposal(null)} disabled={applying}>
              <X size={14} className="mr-1" /> Discard
            </Button>
            <Button onClick={apply} disabled={applying}
              style={{ background: "hsl(142 70% 40%)", color: "white" }}>
              {applying ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
              Apply & save revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditorialTools;

// ---------- helpers ----------

function ProposalView({
  proposal, article, pickedTitle, pickedMeta, onPickTitle, onPickMeta,
}: {
  proposal: Proposal;
  article: ArticleRow;
  pickedTitle: string | null;
  pickedMeta: string | null;
  onPickTitle: (s: string) => void;
  onPickMeta: (s: string) => void;
}) {
  const p = proposal.proposal as Record<string, unknown>;
  const t = proposal.tool;

  if (t === "title_alternatives") {
    const options = (p.options as string[]) ?? [];
    return (
      <div>
        <Label>Current</Label>
        <CurrentBox value={article.seo_title} />
        <Label>Options</Label>
        <div className="space-y-1.5">
          {options.map((o, i) => (
            <PickRow key={i} value={o} length picked={pickedTitle === o} onPick={() => onPickTitle(o)} />
          ))}
        </div>
      </div>
    );
  }
  if (t === "meta_optimize") {
    const options = (p.options as string[]) ?? [];
    return (
      <div>
        <Label>Current</Label>
        <CurrentBox value={article.meta_description} />
        <Label>Options</Label>
        <div className="space-y-1.5">
          {options.map((o, i) => (
            <PickRow key={i} value={o} length picked={pickedMeta === o} onPick={() => onPickMeta(o)} />
          ))}
        </div>
      </div>
    );
  }
  if (t === "rewrite_intro") {
    const before = article.content?.[0]?.paragraphs?.[0] ?? "";
    return <DiffBox before={before} after={String(p.rewritten ?? "")} notes={p.notes as string} />;
  }
  // section-based: humanize / eeat / cta / snippet
  const idx = proposal.section_index ?? 0;
  const before = (article.content?.[idx]?.paragraphs ?? []).join("\n\n");
  const after = ((p.paragraphs as string[]) ?? []).join("\n\n");
  const headingNote = p.heading ? `New heading: ${p.heading}\n\n` : "";
  return <DiffBox before={before} after={headingNote + after} notes={p.notes as string} />;
}

function buildPatch(
  proposal: Proposal,
  article: ArticleRow,
  picks: { pickedTitle: string | null; pickedMeta: string | null },
): Record<string, unknown> | null {
  const t = proposal.tool;
  const p = proposal.proposal as Record<string, unknown>;

  if (t === "title_alternatives") return picks.pickedTitle ? { seo_title: picks.pickedTitle } : null;
  if (t === "meta_optimize") return picks.pickedMeta ? { meta_description: picks.pickedMeta } : null;

  if (t === "rewrite_intro") {
    const next = structuredClone(article.content ?? []);
    if (!next[0]) return null;
    next[0].paragraphs = [String(p.rewritten ?? ""), ...next[0].paragraphs.slice(1)];
    return { content: next };
  }

  const idx = proposal.section_index ?? 0;
  const next = structuredClone(article.content ?? []);
  if (!next[idx]) return null;
  if (Array.isArray(p.paragraphs)) next[idx].paragraphs = p.paragraphs as string[];
  if (typeof p.heading === "string" && p.heading.trim()) next[idx].heading = p.heading;
  return { content: next };
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 mt-3" style={{ color: "hsl(0 0% 55%)" }}>
    {children}
  </div>
);
const CurrentBox = ({ value }: { value: string }) => (
  <pre className="text-xs whitespace-pre-wrap rounded-lg border p-3 mb-1"
    style={{ background: "hsl(0 0% 4%)", borderColor: "hsl(0 0% 16%)", color: "hsl(0 0% 70%)" }}>
    {value || "(empty)"}
  </pre>
);
const PickRow = ({ value, picked, onPick, length }: { value: string; picked: boolean; onPick: () => void; length?: boolean }) => (
  <button onClick={onPick} className="w-full text-left rounded-lg border p-2.5 text-xs transition-colors"
    style={{
      background: picked ? "hsl(142 70% 40% / 0.15)" : "hsl(0 0% 5%)",
      borderColor: picked ? "hsl(142 70% 40% / 0.5)" : "hsl(0 0% 16%)",
      color: "hsl(0 0% 92%)",
    }}>
    <div className="flex items-start gap-2">
      {picked && <Check size={12} style={{ color: "hsl(142 70% 60%)" }} className="mt-0.5" />}
      <span className="flex-1">{value}</span>
      {length && <span className="text-[10px] opacity-60">{value.length}</span>}
    </div>
  </button>
);
const DiffBox = ({ before, after, notes }: { before: string; after: string; notes?: string }) => (
  <div className="space-y-3">
    {notes && <p className="text-xs italic" style={{ color: "hsl(207 68% 70%)" }}>{notes}</p>}
    <div className="grid md:grid-cols-2 gap-3">
      <div>
        <Label>Before</Label>
        <pre className="text-xs whitespace-pre-wrap rounded-lg border p-3"
          style={{ background: "hsl(0 0% 4%)", borderColor: "hsl(0 0% 16%)", color: "hsl(0 0% 65%)" }}>{before || "(empty)"}</pre>
      </div>
      <div>
        <Label>After</Label>
        <pre className="text-xs whitespace-pre-wrap rounded-lg border p-3"
          style={{ background: "hsl(142 30% 8%)", borderColor: "hsl(142 50% 30%)", color: "hsl(0 0% 92%)" }}>{after || "(empty)"}</pre>
      </div>
    </div>
  </div>
);
